// Cost dashboard for the admin console: spend across ALL APIs and ALL users
// (generation runs + interactive marking + field-engine deepen), summed over a
// rolling window. Read-only. `raw` = provider cost; `charged` = raw × markup.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  getCosts,
  type AdminCostsResponse,
  type CostBreakdownRow,
} from "@/api/admin-costs";
import { Alert, Spinner, cx } from "@/components/ui";

const WINDOWS = [7, 30, 90] as const;

function usd(v: number): string {
  if (!v) return "$0";
  if (v < 1) return `$${v.toFixed(4)}`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function compact(n: number): string {
  return n.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

const SOURCE_LABEL: Record<string, string> = {
  generation: "Question generation",
  marking: "Study marking",
  deepen: "Field deepen",
  figure: "Figures / images",
  report_caption: "Figure captioning",
  classify: "Background classify",
  image: "Image generation",
};

const CATEGORY_LABEL: Record<string, string> = {
  input: "Input",
  output: "Output",
  reasoning: "Reasoning (thinking)",
  cached_read: "Cached input",
  cached_write: "Cache write",
  tool: "Tool",
  images: "Images",
};

export function AdminCostsSection() {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const q = useQuery({
    queryKey: ["admin-costs", days],
    queryFn: () => getCosts(days),
  });

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{t("API costs")}</h2>
          <p className="mt-1 text-sm text-ink-faint">
            {t("What the app is spending on AI, across every user.")}
          </p>
        </div>
        <div className="flex shrink-0 gap-1 rounded-full bg-black/5 p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDays(w)}
              className={cx(
                "rounded-full px-2.5 py-1 text-xs font-semibold transition",
                days === w
                  ? "bg-white text-ink shadow-sm"
                  : "text-ink-faint hover:text-ink",
              )}
            >
              {t("{{count}}d", { count: w })}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-8 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      ) : q.isError || !q.data ? (
        <div className="mt-4">
          <Alert>{t("Couldn't load costs. Try again.")}</Alert>
        </div>
      ) : (
        <CostsBody data={q.data} />
      )}
    </div>
  );
}

function CostsBody({ data }: { data: AdminCostsResponse }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-2 gap-3">
        <Stat
          label={t("Provider cost")}
          value={usd(data.total_raw_usd)}
          hint={t("Actual API spend")}
        />
        <Stat
          label={t("Billed (×{{markup}})", { markup: data.markup })}
          value={usd(data.total_charged_usd)}
          hint={t("With markup")}
          accent
        />
      </div>

      <DayChart data={data} />

      <Breakdown
        title={t("By source")}
        rows={data.by_source}
        labelFor={(k) => t(SOURCE_LABEL[k] ?? k)}
      />
      <Breakdown title={t("By model")} rows={data.by_model} labelFor={(k) => k} />

      {data.by_category.length > 0 ? (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {t("Token breakdown")}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data.by_category.map((c) => (
              <span
                key={c.key}
                className="rounded-full bg-black/5 px-2.5 py-1 text-xs text-ink-soft"
              >
                {t(CATEGORY_LABEL[c.key] ?? c.key)}:{" "}
                <span className="font-semibold tabular-nums text-ink">{compact(c.tokens)}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Top spenders */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
          {t("Top users")}
        </div>
        {data.by_user.length === 0 ? (
          <p className="mt-2 text-sm text-ink-faint">{t("No spend in this window.")}</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {data.by_user.slice(0, 10).map((u) => (
              <li
                key={u.user_id}
                className="flex items-center justify-between gap-3 rounded-lg border border-black/5 bg-white px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink" title={u.email}>
                  {u.email}
                </span>
                <span className="shrink-0 text-xs text-ink-faint">
                  {t("{{count}} calls", { count: u.calls })}
                </span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">
                  {usd(u.charged_usd)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-xl border p-3",
        accent ? "border-brand-200 bg-brand-50" : "border-black/5 bg-white",
      )}
    >
      <div className="text-xs font-medium text-ink-faint">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-ink">{value}</div>
      <div className="mt-0.5 text-[11px] text-ink-faint">{hint}</div>
    </div>
  );
}

// Inline SVG bar chart (charged spend per day). No external chart dep — CSP-safe.
function DayChart({ data }: { data: AdminCostsResponse }) {
  const { t } = useTranslation();
  const points = data.by_day;
  if (points.length === 0) {
    return <p className="text-sm text-ink-faint">{t("No spend in this window.")}</p>;
  }
  const max = Math.max(...points.map((p) => p.charged_usd), 0.0000001);
  const W = 100;
  const H = 36;
  const gap = 0.5;
  const bw = Math.max((W - gap * (points.length - 1)) / points.length, 0.4);
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("Daily spend (billed)")}
      </div>
      <svg
        className="mt-2 w-full"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ height: "72px" }}
        role="img"
        aria-label={t("Daily spend chart")}
      >
        {points.map((p, i) => {
          const h = (p.charged_usd / max) * (H - 2);
          const x = i * (bw + gap);
          return (
            <rect
              key={p.date}
              x={x}
              y={H - h}
              width={bw}
              height={h || 0.3}
              rx={0.4}
              className="fill-brand-500"
            >
              <title>{`${p.date}: ${usd(p.charged_usd)}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-ink-faint">
        <span>{points[0]?.date}</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function Breakdown({
  title,
  rows,
  labelFor,
}: {
  title: string;
  rows: CostBreakdownRow[];
  labelFor: (key: string) => string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {title}
      </div>
      <ul className="mt-2 space-y-1">
        {rows.map((r) => (
          <li
            key={r.key}
            className="flex items-center justify-between gap-3 rounded-lg border border-black/5 bg-white px-3 py-2"
          >
            <span className="min-w-0 flex-1 truncate text-sm text-ink" title={r.key}>
              {labelFor(r.key)}
            </span>
            <span className="shrink-0 text-xs text-ink-faint">
              {t("{{count}} calls", { count: r.calls })} · {compact(r.tokens)} tok
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">
              {usd(r.charged_usd)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
